import React, { RefObject, HTMLAttributes, useState, useCallback, useRef } from 'react';
import { Input } from '../Input';
import { Icon } from '../Icon';
import { Button } from '../Button';
import { InputProps } from '../Input';
import { classNames } from '../../utilities';
import './SearchBox.scss';

export interface SearchBoxProps extends HTMLAttributes<HTMLInputElement> {
  /** required for accessibility if no labels */
  'aria-label'?: string;
  /* Attach a custom class to the search container */
  containerClassName?: string;
  /* Rendered text in the search box for uncontrolled component */
  defaultValue?: string;
  /* Whether to disable searchbox */
  disabled?: boolean;
  /* Specify an ID for the search input */
  id?: string;
  /** Custom class for input  */
  inputClassName?: string;
  /* key for a label */
  name?: string;
  /* Custom hint text */
  placeholder?: string;
  /* What is entered into the search box upon render */
  value?: string;
  /* Function that gets called when input is cleared */
  onClear?: () => void;
  /* Function that gets called whenever input changes */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, value?: string) => void;
  /* Function that gets called when enter or search button is pressed */
  onSearch?: (searchInput?: string) => void;
}

interface SearchBoxInputProps extends InputProps {
  ref?: RefObject<HTMLInputElement>;
}

/**
 * Generic Search Box
 */
const SearchBox = (props: SearchBoxProps) => {
  const {
    containerClassName,
    defaultValue,
    disabled,
    inputClassName,
    id,
    name,
    placeholder = 'Search',
    value,
    onChange,
    onSearch,
    onClear,
  } = props;

  if (defaultValue !== undefined && value !== undefined) {
    throw new Error('Inputs should either be controlled (value) or uncontrolled (defaultValue).');
  }

  const [uncontrolledSearchInput, setUncontrolledSearchInput] = useState(defaultValue);

  const inputRef = useRef<HTMLInputElement>(null);

  // If value prop is passed in even if it's an empty string, assume controlled component.
  const isControlled = value !== undefined && value !== null;
  const inputVal = isControlled ? value : uncontrolledSearchInput;

  const onChangeWrapper = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!isControlled) {
        // if this component is uncontrolled, set the search input for the user
        setUncontrolledSearchInput(event.target.value);
      }
      if (onChange) {
        // If there is an onChange, call it, even if this component is controlled
        onChange(event, event.target.value ?? '');
      }
    },
    [onChange, isControlled]
  );

  const onClearWrapper = useCallback(() => {
    if (!isControlled) {
      setUncontrolledSearchInput('');
      if (inputRef && inputRef.current) inputRef.current.focus();
    }
    if (onClear) {
      onClear();
    }
  }, [onClear, isControlled]);

  const onSearchWrapper = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && onSearch) {
      onSearch(inputVal);
    }
  };

  const inputProps: SearchBoxInputProps = {
    ['aria-label']: props['aria-label'],
    className: classNames('ps-searchbox__input', inputClassName),
    disabled,
    id,
    name,
    placeholder,
    ref: inputRef,
    role: 'searchbox',
    value: inputVal ?? '',
    onChange: onChangeWrapper,
    onKeyDown: onSearchWrapper,
  };

  const containerClassNames = classNames('ps-searchbox', containerClassName);
  const cancelIconClassNames = classNames(
    'ps-searchbox__cancelicon-container',
    !inputVal || (isControlled && !onClear) ? 'ps-searchbox__hidden' : ''
  );

  return (
    <div className={containerClassNames}>
      <span className="ps-searchbox__searchicon-container">
        <Icon className="ps-searchbox__searchicon" name="Search" />
      </span>
      <Input {...inputProps} />
      <Button
        icon="Close"
        variant="icon-only"
        onClick={onClearWrapper}
        className={cancelIconClassNames}
        aria-label="Close"
      />
    </div>
  );
};
export { SearchBox };
